import {
  AutoModelVersion,
  getAutoBrands,
  getAutoModelsByBrandUrl,
  getAutoVersionsByModelUrl,
} from '@nemuru-eng/js-api-client';

import { SelectOption } from '@/types/selectOption';

// FIXME: Need to filter this for EBRO
const BRANDS_BY_MERCHANT_ID = {
  // EBRO
  ['b21a57a2-1662-42f1-a8b2-936db6abb090']: ['Ebro'],
  ['e4cf1153-7568-4d35-b0d1-7757d22e118c']: ['Ebro'],
};

export class AutomobileDataEntity {
  private _merchantId: string;

  public selectedBrand: AutoData | undefined = undefined;
  public selectedModel: AutoData | undefined = undefined;
  public selectedVersion: AutoVersionData | undefined = undefined;
  public brands: AutoData[] = [];
  public brandModels: AutoData[] = [];
  public versions: AutoVersionData[] = [];

  constructor(merchantId: string) {
    this._merchantId = merchantId;
  }

  public async init(
    selectedBrand?: string,
    selectedModel?: string,
    selectedVersion?: string,
  ): Promise<AutomobileDataEntity> {
    if (!this.brands || this.brands.length === 0) {
      this.brands = await this.getBrandsFromCdn();
    }

    if (selectedBrand || selectedModel || selectedVersion) {
      await this.update(selectedBrand, selectedModel, selectedVersion);
    }

    return this;
  }

  public async update(selectedBrand?: string, selectedModel?: string, selectedVersion?: string): Promise<void> {
    if (selectedBrand) {
      await this.updateBrand(selectedBrand);
    }
    if (selectedModel) {
      await this.updateModel(selectedModel);
    }
    if (selectedVersion) {
      await this.updateVersion(selectedVersion);
    }
  }

  public async updateBrand(selectedBrand: string): Promise<AutomobileDataEntity> {
    if (!this.brands.length) return this;
    this.selectedBrand = this.findOption<AutoData>(this.brands, selectedBrand);
    this.brandModels = await this.getBrandModelsFromCdn(this.selectedBrand.url);
    this.versions = [];
    return this;
  }

  public async updateModel(selectedModel: string): Promise<AutomobileDataEntity> {
    if (!this.brandModels.length) return this;
    this.selectedModel = this.findOption<AutoData>(this.brandModels, selectedModel);
    this.versions = await this.getVersionsFromCdn(this.selectedModel.url);
    return this;
  }

  public async updateVersion(selectedVersion: string): Promise<AutomobileDataEntity> {
    if (!this.versions.length) return this;
    this.selectedVersion = this.findOption<AutoVersionData>(this.versions, selectedVersion);
    return this;
  }

  private async getBrandsFromCdn(): Promise<AutoData[]> {
    try {
      const brands = (await getAutoBrands()).map((brand) => ({
        value: brand.value,
        display: brand.value,
        url: brand.url,
      }));
      return this.filterBrandsByAgentId(brands);
    } catch {
      throw new Error('Not able to retrieve Automobile Brands from CDN.');
    }
  }

  private async getBrandModelsFromCdn(brandUrl: string): Promise<AutoData[]> {
    if (!brandUrl) return [];

    try {
      const models = (await getAutoModelsByBrandUrl({ brandUrl })).map((model) => ({
        value: model.value,
        display: model.value,
        url: model.url,
      }));
      return models;
    } catch {
      throw new Error('Not able to retrieve Automobile Models from CDN.');
    }
  }

  private async getVersionsFromCdn(modelUrl: string): Promise<AutoVersionData[]> {
    if (!modelUrl) return [];

    try {
      const versions = (await getAutoVersionsByModelUrl({ modelUrl }))
        .filter((autoAssetDetails) => autoAssetDetails.version)
        .map((autoAssetDetails) => ({
          ...autoAssetDetails,
          value: autoAssetDetails.version as string,
          display: autoAssetDetails.version as string,
        }));
      return versions;
    } catch {
      throw new Error('Not able to retrieve Automobile Model Versions from CDN.');
    }
  }

  private filterBrandsByAgentId(brands: AutoData[]): AutoData[] {
    const brandsByAgentId = BRANDS_BY_MERCHANT_ID[this._merchantId];
    if (!brandsByAgentId) return brands;
    return brands?.filter((brand) => brandsByAgentId.includes(brand.value));
  }

  private findOption<T extends SelectOption>(optionsArray: T[], optionValue: string): T {
    const normalizedOptionValue = optionValue.trim();
    const option =
      optionsArray?.find((candidate) => candidate.value === optionValue) ??
      optionsArray?.find((candidate) => String(candidate.value).trim() === normalizedOptionValue);

    if (!option) {
      console.error(`Not able to find option ${optionValue} in option array: ${optionsArray}`);

      // Useful in cases were we are trying to edit a Cart with a Vehicle that is no longer in the CDN
      // Page won't break and initial values of B/M/V will load
      // (but will not appear in the options dropdown anymore)
      return {
        value: optionValue,
        display: optionValue,
      } as T;
    }

    return option;
  }
}

export interface AutoData extends SelectOption {
  value: string;
  url: string;
}

export interface AutoVersionData extends SelectOption, AutoModelVersion {
  value: string;
}
